import styled from '@emotion/native';
import type {
  ConsumableCreateInputRequest,
  ConsumableCreateRequest,
} from '@quromedical/models';
import { Row } from 'components/base';
import { Button, Card } from 'design-system';
import { MARGIN } from 'design-system/theme';
import { useList } from 'hooks/useList';
import type { ConsumableFetcher } from 'integration';
import { useState } from 'react';

import { ConsumableForm } from './ConsumableForm';

interface FormProps {
  handleSubmit: (data: ConsumableCreateRequest) => Promise<void>;
  consumableFetcher: ConsumableFetcher;
  title?: string;
  patient: string;
  admission: string;
}

type ConsumableFormState = {
  data: Partial<ConsumableCreateInputRequest>;
  hasErrors: boolean;
  hasSubmitted: boolean;
  isDuplicated?: boolean;
};

const ButtonWrapper = styled(Row)({
  marginTop: MARGIN['2xs'],
  width: '100%',
  justifyContent: 'flex-end',
});

export const ConsumableListForm = ({
  handleSubmit,
  consumableFetcher,
  title,
  admission,
  patient,
}: FormProps) => {
  const list = useList<number, ConsumableFormState>([
    {
      key: Math.random(),
      value: {
        data: {},
        hasErrors: true,
        hasSubmitted: false,
        isDuplicated: false,
      },
    },
  ]);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const isDuplicate = (
    newData: Partial<ConsumableCreateInputRequest>,
    currentKey: number,
  ) => {
    const newCode = newData.medpraxCode;
    if (!newCode) return false;

    return list.items.some(
      (item) =>
        item.key !== currentKey && item.value.data.medpraxCode === newCode,
    );
  };

  return (
    <Card unsetZIndex>
      {list.items.map((p, index) => {
        const isSubmittedItem = p.value.hasSubmitted || hasSubmitted;

        return (
          <ConsumableForm
            key={p.key}
            title={title}
            onAdd={() => {
              const isDuplicated = isDuplicate(p.value.data, p.key);

              list.update(p.key, {
                ...p.value,
                hasErrors: false,
                hasSubmitted: true,
                isDuplicated,
              });

              if (!isDuplicated) {
                list.add(Math.random(), {
                  data: {},
                  hasErrors: true,
                  hasSubmitted: false,
                  isDuplicated: false,
                });
              }
            }}
            onChange={(data, hasErrors) =>
              list.update(p.key, {
                hasSubmitted: p.value.hasSubmitted,
                data,
                hasErrors,
                isDuplicated: false,
              })
            }
            onRemove={() => {
              list.remove(p.key);
            }}
            canAdd={index === list.items.length - 1}
            canRemove={list.items.length > 1}
            showErrors={isSubmittedItem && p.value.hasErrors}
            initialValues={p.value.data}
            isDuplicated={p.value.isDuplicated}
            consumableFetcher={consumableFetcher}
          />
        );
      })}
      <ButtonWrapper>
        <Button
          variant="contained"
          onPress={() => {
            setHasSubmitted(true);

            const codes = list.items
              .map((item) => item.value.data.medpraxCode)
              .filter((code): code is string => !!code);

            const hasDuplicates = new Set(codes).size !== codes.length;

            if (hasDuplicates) {
              const duplicateCodes = codes.filter(
                (code, index, arr) => arr.indexOf(code) !== index,
              );

              list.items.forEach((p) => {
                const code = p.value.data.medpraxCode;
                const isDuplicated = code && duplicateCodes.includes(code);
                if (isDuplicated) {
                  list.update(p.key, {
                    ...p.value,
                    hasErrors: false,
                    isDuplicated: true,
                  });
                }
              });

              return;
            }

            const allValid = list.items.every((item) => !item.value.hasErrors);
            if (!allValid) {
              return;
            }

            // casting as the data is known to be valid
            const consumableItems = list.items.map(
              (p) => p.value.data as ConsumableCreateInputRequest,
            );

            const result: ConsumableCreateRequest = {
              consumables: consumableItems,
              patient,
              admission,
            };
            handleSubmit(result);
          }}
          text={'Submit'}
        />
      </ButtonWrapper>
    </Card>
  );
};
